import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Button,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {
  useCreateFieldDefinitionMutation,
  useUpdateFieldDefinitionMutation,
} from '../../app/services/customFields';

const FIELD_TYPES = ['numeric', 'text', 'checkbox', 'radio'];

export default function ModalAddCustomField({ open, onClose, maintenanceId, editField, refetch }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    field_name: '',
    field_type: 'text',
    is_required: false,
    unit: '',
    field_options: [],
    display_order: 0,
  });
  const [optionInput, setOptionInput] = useState('');
  const [errors, setErrors] = useState({});

  const [createField, { isLoading: isCreating }] = useCreateFieldDefinitionMutation();
  const [updateField, { isLoading: isUpdating }] = useUpdateFieldDefinitionMutation();

  useEffect(() => {
    if (editField) {
      setForm({
        field_name: editField.field_name,
        field_type: editField.field_type,
        is_required: editField.is_required,
        unit: editField.unit || '',
        field_options: editField.field_options || [],
        display_order: editField.display_order,
      });
    } else {
      setForm({
        field_name: '',
        field_type: 'text',
        is_required: false,
        unit: '',
        field_options: [],
        display_order: 0,
      });
    }
    setErrors({});
  }, [editField, open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setForm((prev) => ({
        ...prev,
        field_options: [...prev.field_options, optionInput.trim()],
      }));
      setOptionInput('');
    }
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      field_options: prev.field_options.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.field_name.trim()) {
      newErrors.field_name = t('customFields.errors.nameRequired');
    }

    if (form.field_type === 'radio' && form.field_options.length < 2) {
      newErrors.field_options = t('customFields.errors.optionsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      // Remove field_options if not radio type
      const fieldData = { ...form };
      if (fieldData.field_type !== 'radio') {
        delete fieldData.field_options;
      }

      if (editField) {
        await updateField({
          id: editField.id,
          maintenanceId,
          fieldData,
        }).unwrap();
      } else {
        await createField({
          maintenanceId,
          fieldData,
        }).unwrap();
      }
      refetch();
      onClose();
    } catch (error) {
      console.error('Error saving field:', error);
      const errorMessage = error?.data?.message;
      if (Array.isArray(errorMessage)) {
        setErrors({ submit: errorMessage.join(', ') });
      } else {
        setErrors({ submit: errorMessage || t('customFields.errors.saveFailed') });
      }
    }
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      numeric: t('customFields.numeric'),
      text: t('customFields.text'),
      checkbox: t('customFields.checkbox'),
      radio: t('customFields.radio'),
    };
    return types[type] || type;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editField ? t('customFields.editField') : t('customFields.addField')}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('customFields.fieldName')}
            value={form.field_name}
            onChange={(e) => handleChange('field_name', e.target.value)}
            error={!!errors.field_name}
            helperText={errors.field_name}
            fullWidth
            required
          />

          <TextField
            select
            label={t('customFields.fieldType')}
            value={form.field_type}
            onChange={(e) => handleChange('field_type', e.target.value)}
            fullWidth
            required
          >
            {FIELD_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {getFieldTypeLabel(type)}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_required}
                onChange={(e) => handleChange('is_required', e.target.checked)}
              />
            }
            label={t('customFields.required')}
          />

          {form.field_type === 'numeric' && (
            <TextField
              label={t('customFields.unit')}
              value={form.unit}
              onChange={(e) => handleChange('unit', e.target.value)}
              placeholder="e.g., °C, mm, kg"
              fullWidth
            />
          )}

          {form.field_type === 'radio' && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label={t('customFields.addOption')}
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addOption()}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={addOption}
                  startIcon={<AddIcon />}
                >
                  {t('generic.add')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {form.field_options.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onDelete={() => removeOption(index)}
                  />
                ))}
              </Box>
              {errors.field_options && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                  {errors.field_options}
                </Box>
              )}
            </Box>
          )}

          {errors.submit && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {errors.submit}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('generic.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isCreating || isUpdating}
        >
          {editField ? t('generic.save') : t('generic.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
